import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Video, Plus, Sparkles, ClockIcon, Users, FileText, Loader2, ExternalLink,
  PlayCircle, CheckCircle2, AlertCircle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return iso; }
};
const fmtDuration = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const STATUS_COLOR = {
  scheduled: 'bg-sky-100 text-sky-700',
  started:   'bg-emerald-100 text-emerald-700',
  ended:     'bg-slate-100 text-slate-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

const Stat = ({ label, value, tone, testId }) => {
  const tones = {
    default: 'bg-card',
    success: 'bg-emerald-50 border-emerald-200',
    info:    'bg-sky-50 border-sky-200',
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone || 'default']}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-3xl font-semibold mt-1">{value}</p>
    </div>
  );
};

const LMSVirtualMeetPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const role = user?.role;
  const isManager = ['Super Admin', 'CEO', 'Manager'].includes(role);

  const [meetings, setMeetings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);     // meeting detail open
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', leadId: '', leadName: '' });
  const [creating, setCreating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get('/jitsi-meetings?limit=50')];
      if (isManager) reqs.push(api.get('/jitsi-meetings/analytics/overview'));
      const results = await Promise.all(reqs);
      setMeetings(results[0].data.items || []);
      if (results[1]) setAnalytics(results[1].data);
    } catch (_e) {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [api, isManager]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/jitsi-meetings', {
        title: form.title || null,
        leadId: form.leadId || null,
      });
      toast.success('Meeting created');
      setCreateOpen(false);
      setForm({ title: '', leadId: '', leadName: '', scheduledFor: '' });
      navigate(`/lms-meet/room/${data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setCreating(false);
    }
  };

  const handleScheduleForLater = async () => {
    if (!form.scheduledFor) {
      toast.error('Pick a date and time first');
      return;
    }
    setCreating(true);
    try {
      await api.post('/jitsi-meetings', {
        title: form.title || null,
        leadId: form.leadId || null,
        scheduledFor: new Date(form.scheduledFor).toISOString(),
      });
      toast.success('Meeting scheduled — find it in Recent Meetings below');
      setCreateOpen(false);
      setForm({ title: '', leadId: '', leadName: '', scheduledFor: '' });
      loadAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setCreating(false);
    }
  };

  const handleCancelMeeting = async (id) => {
    if (!window.confirm('Cancel this scheduled meeting?')) return;
    try {
      await api.post(`/jitsi-meetings/${id}/status`, { status: 'cancelled' });
      toast.success('Meeting cancelled');
      loadAll();
    } catch {
      toast.error('Failed to cancel meeting');
    }
  };

  const refreshDetail = async (id) => {
    try {
      const { data } = await api.get(`/jitsi-meetings/${id}`);
      setSelected(data);
      // refresh list too
      const { data: list } = await api.get('/jitsi-meetings?limit=50');
      setMeetings(list.items || []);
    } catch (_e) { /* ignore */ }
  };

  return (
    <div className="space-y-6" data-testid="lms-virtual-meet-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Video className="h-7 w-7" /> LMS Virtual Meet
          </h1>
          <p className="text-muted-foreground">
            Host video meetings inside LMS — every recorded meeting gets an automatic
            AI transcript + structured summary attached to the lead.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-meeting-btn" size="lg">
          <Plus className="h-4 w-4 mr-2" /> New Meeting
        </Button>
      </div>

      {/* Manager analytics */}
      {isManager && analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="meet-analytics-row">
          <Stat label="Total Meetings" value={analytics.totals.totalMeetings} tone="info" testId="meet-stat-total" />
          <Stat label="Completed" value={analytics.totals.completedMeetings} tone="success" testId="meet-stat-completed" />
          <Stat label="Total Hours" value={analytics.totals.totalHours} testId="meet-stat-hours" />
          <Stat label="Active Hosts" value={analytics.rows.length} testId="meet-stat-hosts" />
        </div>
      )}

      <Tabs defaultValue="meetings">
        <TabsList>
          <TabsTrigger value="meetings" data-testid="meetings-tab">Meetings</TabsTrigger>
          {isManager && <TabsTrigger value="per-host" data-testid="per-host-tab">Per-Host Breakdown</TabsTrigger>}
        </TabsList>

        <TabsContent value="meetings">
          <Card>
            <CardHeader>
              <CardTitle>Recent Meetings</CardTitle>
              <CardDescription>Click a meeting to view AI transcript + summary.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">
                  <Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Loading…
                </p>
              ) : meetings.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No meetings yet. Create your first meeting!</p>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title / Lead</TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>AI Summary</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Scheduled / Created</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meetings.map((m) => (
                        <TableRow key={m.id} data-testid={`meeting-row-${m.id}`}>
                          <TableCell>
                            <div className="font-medium">{m.title}</div>
                            {m.leadName && <div className="text-xs text-muted-foreground">{m.leadName}</div>}
                          </TableCell>
                          <TableCell>{m.hostName}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLOR[m.status] || 'bg-muted'}>{m.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {m.summaryStatus === 'completed' && (
                              <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                                <Sparkles className="h-3 w-3" /> Ready
                              </Badge>
                            )}
                            {(m.summaryStatus === 'processing' || m.transcriptStatus === 'processing') && (
                              <Badge className="bg-amber-100 text-amber-700 gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" /> Processing
                              </Badge>
                            )}
                            {m.summaryStatus === 'queued' && (
                              <Badge className="bg-sky-100 text-sky-700">Queued</Badge>
                            )}
                            {m.summaryStatus === 'failed' && (
                              <Badge className="bg-rose-100 text-rose-700">Failed</Badge>
                            )}
                            {!m.summaryStatus || m.summaryStatus === 'pending' ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : null}
                          </TableCell>
                          <TableCell>{fmtDuration(m.durationSeconds)}</TableCell>
                          <TableCell className="text-xs">
                            {m.status === 'scheduled' && m.scheduledFor ? (
                              <div>
                                <div className="text-violet-700 font-medium">📅 {fmtDateTime(m.scheduledFor)}</div>
                                <div className="text-muted-foreground">created {fmtDateTime(m.createdAt)}</div>
                              </div>
                            ) : (
                              fmtDateTime(m.createdAt)
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => setSelected(m)} data-testid={`view-meeting-${m.id}`}>
                                View
                              </Button>
                              {m.status !== 'ended' && m.status !== 'cancelled' && (
                                <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => handleCancelMeeting(m.id)} data-testid={`cancel-meeting-${m.id}`}>
                                  Cancel
                                </Button>
                              )}
                              {m.status !== 'ended' && m.status !== 'cancelled' && (
                                <Button size="sm" onClick={() => navigate(`/lms-meet/room/${m.id}`)} data-testid={`join-meeting-${m.id}`}>
                                  <PlayCircle className="h-3 w-3 mr-1" /> Join
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isManager && (
          <TabsContent value="per-host">
            <Card>
              <CardHeader>
                <CardTitle>Per-Host Breakdown</CardTitle>
                <CardDescription>Meetings + total meeting hours per host</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Host</TableHead>
                        <TableHead className="text-right">Total Meetings</TableHead>
                        <TableHead className="text-right">Completed</TableHead>
                        <TableHead className="text-right">Total Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(analytics?.rows || []).map((r) => (
                        <TableRow key={r.hostUserId} data-testid={`host-row-${r.hostUserId}`}>
                          <TableCell className="font-medium">{r.hostName}</TableCell>
                          <TableCell className="text-right font-mono">{r.totalMeetings}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">{r.completedMeetings}</TableCell>
                          <TableCell className="text-right font-mono">{fmtDuration(r.totalDurationSeconds)}</TableCell>
                        </TableRow>
                      ))}
                      {(!analytics?.rows || analytics.rows.length === 0) && (
                        <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No meetings yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Meeting dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-meeting-dialog">
          <DialogHeader>
            <DialogTitle>New Meeting</DialogTitle>
            <DialogDescription>Creates a Jitsi room you can share with attendees</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="mtitle">Title</Label>
              <Input
                id="mtitle"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Customer demo — XYZ Pvt Ltd"
                data-testid="meeting-title-input"
              />
            </div>
            <div>
              <Label htmlFor="mlead">Lead ID (optional — link summary to a lead)</Label>
              <Input
                id="mlead"
                value={form.leadId}
                onChange={(e) => setForm({ ...form, leadId: e.target.value })}
                placeholder="leave blank for standalone meeting"
                data-testid="meeting-lead-id-input"
              />
            </div>
            <div>
              <Label htmlFor="mschedule">Date &amp; Time (required only for "Schedule for Later")</Label>
              <Input
                id="mschedule"
                type="datetime-local"
                value={form.scheduledFor || ''}
                onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })}
                data-testid="meeting-schedule-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} data-testid="meeting-cancel-btn">Cancel</Button>
            <Button variant="secondary" onClick={handleScheduleForLater} disabled={creating} data-testid="meeting-schedule-btn">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Schedule for Later
            </Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="meeting-create-btn">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Video className="h-4 w-4 mr-2" />}
              Create &amp; Join
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="meeting-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.title}</DialogTitle>
            <DialogDescription>
              Host: {selected?.hostName} · {fmtDateTime(selected?.createdAt)}
              {selected?.leadName && ` · Lead: ${selected?.leadName}`}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge className={STATUS_COLOR[selected.status] || 'bg-muted'}>{selected.status}</Badge>
                {selected.durationSeconds > 0 && (
                  <Badge variant="outline"><ClockIcon className="h-3 w-3 mr-1" /> {fmtDuration(selected.durationSeconds)}</Badge>
                )}
                {selected.audioAvailable ? (
                  <Badge variant="outline" className="text-emerald-700">Audio on server (until {fmtDateTime(selected.audioExpiresAt)})</Badge>
                ) : selected.audioPurgedAt ? (
                  <Badge variant="outline" className="text-muted-foreground">Audio purged (7-day retention)</Badge>
                ) : null}
              </div>

              {/* AI Summary */}
              {selected.summary && (
                <Card className="border-emerald-300" data-testid="meeting-summary-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-emerald-700">
                      <Sparkles className="h-5 w-5" /> AI Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {selected.summary.shortSummary && (
                      <p className="italic text-muted-foreground">&ldquo;{selected.summary.shortSummary}&rdquo;</p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Interest</p>
                        <p className="font-medium">{selected.summary.interestLevel || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Sentiment</p>
                        <p className="font-medium">{selected.summary.overallSentiment || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Budget</p>
                        <p className="font-medium">{selected.summary.budgetMentioned || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Model</p>
                        <p className="font-medium">{selected.summary.modelDiscussed || '—'}</p>
                      </div>
                    </div>
                    {(selected.summary.concerns?.length > 0) && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Concerns</p>
                        <ul className="list-disc list-inside space-y-1">
                          {selected.summary.concerns.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      </div>
                    )}
                    {(selected.summary.questionsAsked?.length > 0) && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Questions Asked</p>
                        <ul className="list-disc list-inside space-y-1">
                          {selected.summary.questionsAsked.map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                      </div>
                    )}
                    {selected.summary.nextAction && (
                      <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
                        <p className="text-xs uppercase tracking-wider text-emerald-700">Next Action</p>
                        <p className="font-medium text-emerald-900">{selected.summary.nextAction}</p>
                        {selected.summary.followUpDate && (
                          <p className="text-xs text-emerald-700">Follow-up: {selected.summary.followUpDate}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Transcript */}
              {selected.transcript && (
                <Card data-testid="meeting-transcript-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4" /> Transcript
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap max-h-60 overflow-y-auto bg-muted/40 p-3 rounded">
                      {selected.transcript}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Processing states */}
              {(selected.summaryStatus === 'processing' || selected.transcriptStatus === 'processing' || selected.summaryStatus === 'queued') && (
                <Alert>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertTitle>AI processing in progress</AlertTitle>
                  <AlertDescription>
                    Whisper transcription + Claude summary typically takes 1–3 minutes.
                    <Button size="sm" variant="link" onClick={() => refreshDetail(selected.id)} className="ml-2 px-0">Refresh</Button>
                  </AlertDescription>
                </Alert>
              )}
              {selected.summaryStatus === 'failed' && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>AI processing failed</AlertTitle>
                  <AlertDescription className="space-y-2">
                    {selected.audioAvailable && (
                      <Button size="sm" onClick={async () => {
                        try {
                          await api.post(`/jitsi-meetings/${selected.id}/process`);
                          toast.success('Re-queued');
                          setTimeout(() => refreshDetail(selected.id), 1500);
                        } catch (_e) { toast.error('Retry failed'); }
                      }}>Retry</Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            {selected && selected.status !== 'ended' && selected.status !== 'cancelled' && (
              <Button onClick={() => { setSelected(null); navigate(`/lms-meet/room/${selected.id}`); }}>
                <PlayCircle className="h-4 w-4 mr-2" /> Join
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="detail-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LMSVirtualMeetPage;
